// ============================================================================
// CAMP BAND — long-narrow variant bars that span multiple days
// ============================================================================
// Each gym row shows non-camp events in the day cells (top zone) and camp
// variants here as horizontal bars spanning their start_date → end_date.
// One bar per variant. Designed for the "Jayme's Pick" combo layout drawn in
// mockups/calendar-camp-display-options.html (May 2026).
//
// Inputs:
//   campEvents      — array of CAMP-type events for ONE gym, already filtered
//                     to the visible month
//   displayDates    — array of day numbers visible in the current calendar
//                     view (e.g. [1,2,...,31] for full month)
//   currentYear, currentMonth — for cross-month edge cases
//   onEventSelect   — fires when a bar is clicked (opens side panel)
// ============================================================================

import React from 'react';
import { iclassCampId, useCampOccupancy } from '../../lib/iclassCamp';
import { parseYmdLocal, getActualEndDate } from './utils';
import { inferErrorCategory, isErrorAcknowledgedAnywhere } from '../../lib/validationHelpers';

// Decide whether a variant should show the data-error indicator.
// Mirrors the EventCard.js logic: filter out sold_out, drop anything
// acknowledged via patterns, then keep only data_error category.
function variantHasDataErrors(variant, acknowledgedPatterns) {
  const all = variant.validation_errors || [];
  if (all.length === 0) return false;
  const active = all.filter(
    err => err.type !== 'sold_out' && !isErrorAcknowledgedAnywhere(variant, err.message, acknowledgedPatterns)
  );
  return active.some(err => inferErrorCategory(err) === 'data_error');
}

// "Needs attention" check for Errors Focus mode: data errors OR a
// missing/flyer-only description. AI review lives in the Errors tab, not the
// calendar (Jayme, July 2).
function variantHasIssue(variant, acknowledgedPatterns) {
  if (variantHasDataErrors(variant, acknowledgedPatterns)) return true;
  return variant.description_status === 'none' || variant.description_status === 'flyer_only';
}

// Group variants by their (start_date, end_date) so all variants of the same
// camp run land together in the same row block.
function groupCampsByRun(campEvents) {
  const groups = new Map();
  campEvents.forEach(e => {
    const key = `${e.start_date || e.date}-${e.end_date || e.date}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(e);
  });
  return Array.from(groups.values());
}

// Sort variants inside a single camp run for stable visual order.
// Gymnastics first, then Ninja. Within each, Full before Half.
function variantSortKey(event) {
  const text = `${event.title || ''} ${event.program_name || ''}`.toLowerCase();
  let typeRank = 1; // default = gymnastics-ish
  if (text.includes('ninja')) typeRank = 2;
  let durationRank = 0; // full day default
  if (text.includes('half')) durationRank = 1;
  return typeRank * 10 + durationRank;
}

// Which iClass camp family this bar belongs to. Drives the bar COLOR so Summer
// Camp and School Year Camps read as visually distinct categories.
//
// Source of truth = CAMP TYPE (the iClass booking category, `camp_type`), NOT
// the Program. iClass has two independent fields: the Camp Type decides which
// SECTION families browse the camp under, while the Program is a separate
// back-end tag that can drift out of sync (real case: SGT Fall Break camps —
// Camp Type = School Year, Program = Summer). The Camp Type is what families
// actually see, so the calendar mirrors it. A Camp-Type-vs-Program mismatch is
// caught separately in the Errors tab, not painted here. (Jayme, Aug 2026.)
// Falls back to program_name only if camp_type isn't populated yet.
function campProgramCategory(event) {
  const src = (event.camp_type || event.program_name || '').toUpperCase();
  if (src.includes('SCHOOL YEAR')) return 'SCHOOL_YEAR';
  if (src.includes('SUMMER')) return 'SUMMER';
  return 'OTHER'; // e.g. XCEL — show it, don't force it into a camp bucket
}

// One palette per program. Dark text on light fill (contrast-safe on the
// light calendar surface).
const PROGRAM_PALETTE = {
  SUMMER:      { bg: '#fff3cf', border: '#e0b93a', color: '#6b4f1f' }, // sunny gold
  SCHOOL_YEAR: { bg: '#e2edfb', border: '#5a8fd0', color: '#1e3a5f' }, // cool blue
  OTHER:       { bg: '#ececec', border: '#9a9a9a', color: '#333333' }, // neutral
};

// Visual classification used by the bar styling. Color = program (top-level
// category); icon = gymnastics vs ninja (the within-week variant).
function variantStyle(event) {
  const pal = PROGRAM_PALETTE[campProgramCategory(event)] || PROGRAM_PALETTE.OTHER;
  const text = `${event.title || ''} ${event.program_name || ''}`.toLowerCase();
  const isNinja = text.includes('ninja');
  return { ...pal, icon: isNinja ? '🥷' : '🤸' };
}

// "Gym Full Day" / "Ninja Half Day" / etc. — short, always-fits label.
function variantLabel(event) {
  const text = `${event.title || ''} ${event.program_name || ''}`.toLowerCase();
  const isNinja = text.includes('ninja');
  const isHalf = text.includes('half');
  return `${isNinja ? 'Ninja' : 'Gym'} ${isHalf ? 'Half Day' : 'Full Day'}`;
}

// What the bar prints: the icon already says gym vs ninja, so the text only needs Full / Half -
// short enough to fit beside the spots number in a one-week bar (it was cut to "G..." before).
function barLabel(event) {
  const text = `${event.title || ''} ${event.program_name || ''}`.toLowerCase();
  return text.includes('half') ? 'Half' : 'Full';
}

// Capacity status — same logic as EventCard.js
function spotsStatus(event) {
  if (event.has_openings === false) return { kind: 'full', text: '🔴 FULL' };
  const o = event.openings;
  if (o == null) return null;
  if (o === 0) return { kind: 'full', text: '🔴 FULL' };
  if (o <= 3) return { kind: 'low', text: `⚠️ ${o} left` };
  return { kind: 'open', text: `🟢 ${o} left` };
}

const STATUS_COLORS = {
  open: '#2f7c39',
  low: '#b07020',
  full: '#c0392b',
};

export default function CampBand({
  campEvents,
  displayDates,
  currentYear,
  currentMonth,
  onEventSelect,
  acknowledgedPatterns = [],
  errorFocus = false,
}) {
  // Per-day counts from the signed-in setup read (only for gyms that have been read)
  const occupancyByCamp = useCampOccupancy(campEvents);
  if (!campEvents || campEvents.length === 0) return null;

  const runs = groupCampsByRun(campEvents).map(run =>
    run.slice().sort((a, b) => variantSortKey(a) - variantSortKey(b))
  );

  // Determine the "lane" a variant occupies. Same lane = same row in the
  // band, regardless of which week it belongs to. This way a summer of
  // 4 camp weeks (each with Gym Full / Gym Half / Ninja Full / Ninja Half)
  // renders as 4 ROWS total, not 16 — every "Gym Full" bar sits on row 1,
  // every "Gym Half" on row 2, etc., with their gridColumn anchored to
  // each week's actual dates.
  // Lane order: Gym Full, Gym Half, Ninja Full, Ninja Half, then anything
  // unrecognized in insertion order beyond.
  const KNOWN_LANES = {
    'gym-full':   0,
    'gym-half':   1,
    'ninja-full': 2,
    'ninja-half': 3,
  };
  const laneKeyForVariant = (event) => {
    const text = `${event.title || ''} ${event.program_name || ''}`.toLowerCase();
    const isNinja = text.includes('ninja');
    const isHalf  = text.includes('half');
    // Camps hidden from the portal get their own rows under the live ones, so a hidden copy of a
    // live week never sits on top of it.
    return `${event.not_on_portal ? 'hidden-' : ''}${isNinja ? 'ninja' : 'gym'}-${isHalf ? 'half' : 'full'}`;
  };

  // First pass: figure out which lanes are actually used and assign each a
  // contiguous row index (so we don't render empty gaps).
  const usedLaneKeys = new Set();
  for (const run of runs) {
    for (const variant of run) {
      usedLaneKeys.add(laneKeyForVariant(variant));
    }
  }
  // Sort the used lane keys by KNOWN_LANES priority; unknown keys go after.
  const sortedLaneKeys = Array.from(usedLaneKeys).sort((a, b) => {
    const aRank = a in KNOWN_LANES ? KNOWN_LANES[a] : 100;
    const bRank = b in KNOWN_LANES ? KNOWN_LANES[b] : 100;
    return aRank - bRank;
  });
  const laneToRow = {};
  sortedLaneKeys.forEach((key, idx) => { laneToRow[key] = idx + 1; });

  // Second pass: place every variant in its lane's row, with the date-range
  // gridColumn computed per-variant.
  const placements = [];
  for (const run of runs) {
    for (const variant of run) {
      const startDateStr = variant.start_date || variant.date;
      if (!startDateStr) continue;

      const startObj = parseYmdLocal(startDateStr);
      const endObj = getActualEndDate(variant, parseYmdLocal);

      // Map the camp's start/end onto the visible columns. displayDates holds
      // the day-numbers actually on screen for THIS view — full month, a half,
      // a week, or Today→End-of-Month — so the window can start on ANY day, not
      // just the 1st. Convert each end of the run to a coordinate in the
      // current month's day-space: an earlier month is -Infinity, a later
      // month is +Infinity, otherwise its day number.
      const dayCoord = (d) => {
        const y = d.getFullYear(), m = d.getMonth();
        if (y < currentYear || (y === currentYear && m < currentMonth)) return -Infinity;
        if (y > currentYear || (y === currentYear && m > currentMonth)) return Infinity;
        return d.getDate();
      };
      const startCoord = dayCoord(startObj);
      const endCoord = dayCoord(endObj);
      const firstDay = displayDates[0];
      const lastDay = displayDates[displayDates.length - 1];

      // Skip runs that don't overlap the visible window at all — e.g. a camp
      // that already ended before "today" in the Today→End-of-Month view, or a
      // second-half camp while viewing Days 1-15. Without this an out-of-window
      // run used to stretch edge-to-edge and look like a month-long camp.
      if (endCoord < firstDay || startCoord > lastDay) continue;

      // Clip the visible portion into the window: a camp that starts before the
      // window begins at column 0; one that ends after it runs to the last col.
      const startIdx = startCoord <= firstDay ? 0 : displayDates.indexOf(startCoord);
      const endIdx = endCoord >= lastDay ? displayDates.length - 1 : displayDates.indexOf(endCoord);

      // indexOf can only miss if displayDates were non-contiguous (no current
      // view is), but guard so a stray value never renders a broken bar.
      if (startIdx === -1 || endIdx === -1) continue;

      // Grid columns 1-indexed; CSS grid end is exclusive so end is +2.
      const gridColumn = `${startIdx + 1} / ${endIdx + 2}`;

      // Per-day counts for this camp, laid onto the same visible day columns the bar spans.
      // A day the camp doesn't meet (weekend inside a 2-week camp) stays an empty cell.
      const occ = occupancyByCamp[`${variant.gym_id}:${iclassCampId(variant)}`];
      let dayCells = null;
      if (occ) {
        const byDay = {};
        occ.forEach((d) => {
          const dt = parseYmdLocal(String(d.sqlDate).slice(0, 10));
          if (dt.getFullYear() === currentYear && dt.getMonth() === currentMonth) byDay[dt.getDate()] = d;
        });
        dayCells = displayDates.slice(startIdx, endIdx + 1).map((dayNum) => byDay[dayNum] || null);
        const opens = dayCells.filter(Boolean).map((d) => Number(d.open || 0));
        const daysDiffer = new Set(opens).size > 1;
        // "26 26 26 26 26" says one number five times - only show a cell per day when the camp sells
        // single days or a day really differs (say it once, Jayme 2026-11-04)
        if (!opens.length || !(variant.allow_choose_days || daysDiffer)) {
          dayCells = opens.length ? { single: Math.min(...opens) } : null;
        }
      }

      placements.push({
        variant,
        gridColumn,
        span: endIdx - startIdx + 1,
        gridRow: laneToRow[laneKeyForVariant(variant)],
        dayCells,
      });
    }
  }

  if (placements.length === 0) return null;

  const totalRows = sortedLaneKeys.length;
  const anyPerDay = placements.some((pl) => Array.isArray(pl.dayCells));

  return (
    <div
      className="camp-band"
      style={{
        gridColumn: '2 / -1',
        display: 'grid',
        gridTemplateColumns: `repeat(${displayDates.length}, 1fr)`,
        gridTemplateRows: `repeat(${totalRows}, ${anyPerDay ? 46 : 28}px)`,
        gap: '3px',
        padding: '3px 4px 4px',
        // Subtle bg tint that matches the gym cell's gray-50 — visually
        // claims this strip as part of the same gym row rather than letting
        // it look like a new row starting. No top border (dashed line was
        // confusing — read as a row divider).
        background: '#f9fafb',
      }}
    >
      {placements.map(({ variant, gridColumn, gridRow, dayCells, span }) => {
        const style = variantStyle(variant);
        // With per-day counts the bar's status is the TIGHTEST day (the public number is day 1 only)
        const perDayCells = Array.isArray(dayCells) ? dayCells : null;
        const met = perDayCells
          ? perDayCells.filter(Boolean).map((d) => Number(d.open || 0))
          : (dayCells && dayCells.single != null ? [dayCells.single] : []);
        const status = met.length
          ? (Math.min(...met) === 0
              ? { kind: 'full', text: met.every((o) => o === 0) ? '🔴 FULL' : '🔴 a day FULL' }
              : Math.min(...met) <= 3
                ? { kind: 'low', text: `⚠️ ${Math.min(...met)} left` }
                : { kind: 'open', text: `🟢 ${Math.min(...met)} left` })
          : spotsStatus(variant);
        const statusColor = status ? STATUS_COLORS[status.kind] : null;
        // Error indicators (red border / dot) ONLY in Errors Focus mode —
        // normal calendar view stays clean (Jayme).
        const hasErrors = errorFocus && variantHasDataErrors(variant, acknowledgedPatterns);
        // Errors Focus: dim clean variants, leave flagged ones bright.
        const hasIssue = errorFocus ? variantHasIssue(variant, acknowledgedPatterns) : false;
        const dimmed = errorFocus && !hasIssue;
        // A one- or two-day camp (a holiday camp) gets one or two day columns - too narrow for
        // "🤸 Full ⚠️ 2 left" in a row, which used to clip to an empty box (EST Veterans Day,
        // Jayme 2026-11-04). Narrow bars stack instead: Full/Half on top, the spots number under it.
        const narrow = span <= 2;
        // Built in iClass but hidden from the portal: faded + dashed + tagged (Jayme 2026-11-04).
        const notOnPortal = !!variant.not_on_portal;
        const minOpen = met.length ? Math.min(...met) : (variant.openings != null ? Number(variant.openings) : null);

        return (
          <div
            key={variant.id}
            className="camp-bar"
            style={{
              gridColumn,
              gridRow,
              borderRadius: '7px',
              ...(dimmed ? { opacity: 0.25, filter: 'grayscale(1)' } : notOnPortal ? { opacity: 0.55 } : {}),
              // When a variant has unresolved data errors, switch the border
              // to red and add a soft red glow so the bar pops as "needs
              // attention" without redrawing the whole bar.
              border: hasErrors ? '2px solid #dc3545' : notOnPortal ? '2px dashed #4b3f40' : `1.5px solid ${style.border}`,
              background: style.bg,
              color: style.color,
              padding: narrow ? '2px 2px' : perDayCells ? '2px 6px 3px' : '0 8px',
              display: 'flex',
              flexDirection: narrow || perDayCells ? 'column' : 'row',
              alignItems: narrow ? 'center' : perDayCells ? 'stretch' : 'center',
              justifyContent: narrow ? 'center' : 'space-between',
              gap: narrow ? 1 : undefined,
              fontSize: '11px',
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: hasErrors
                ? '0 0 0 2px rgba(220,53,69,0.18), 0 1px 2px rgba(0,0,0,0.06)'
                : '0 1px 2px rgba(0,0,0,0.06)',
              minWidth: 0,
              // narrow bars let the spots pill spill a few pixels past the edge rather than cut it
              overflow: narrow ? 'visible' : 'hidden',
              position: 'relative',
            }}
            onClick={() => onEventSelect && onEventSelect(variant)}
            title={
              notOnPortal ? `NOT ON PORTAL - built in iClass, parents cannot see it. ${variant.title || ''}` :
              hasErrors
                ? `⚠️ Has data error(s) — click for details. ${variant.title || variantLabel(variant)}`
                : (variant.title || variantLabel(variant))
            }
          >
            {hasErrors && (
              <span
                style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  width: '12px',
                  height: '12px',
                  background: '#dc3545',
                  borderRadius: '50%',
                  border: '1.5px solid white',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  zIndex: 2,
                }}
                title="Data error — click to see what's wrong"
              />
            )}
            {narrow ? (
              <>
                <span style={{ fontSize: 9.5, fontWeight: 900, lineHeight: '12px', whiteSpace: 'nowrap' }}>
                  {barLabel(variant)}
                </span>
                {perDayCells && perDayCells.length > 1 ? (
                  <div style={{ display: 'flex', gap: 2 }}>
                    {perDayCells.map((d, i) => {
                      const o = d ? Number(d.open || 0) : null;
                      return (
                        <span key={i} style={{
                          fontSize: 10.5, fontWeight: 900, lineHeight: '15px', padding: '0 3px', borderRadius: 4,
                          background: o === 0 ? '#c0392b' : o != null && o <= 3 ? '#fde7c8' : 'rgba(255,255,255,.85)',
                          color: o === 0 ? '#ffffff' : o != null && o <= 3 ? '#8a4b08' : '#1f2a33',
                        }}>
                          {o == null ? '' : o === 0 ? 'FULL' : o}
                        </span>
                      );
                    })}
                  </div>
                ) : minOpen != null ? (
                  <span style={{
                    fontSize: 10.5, fontWeight: 900, lineHeight: '15px', padding: '0 3px', borderRadius: 4,
                    whiteSpace: 'nowrap',
                    background: minOpen === 0 ? '#c0392b' : minOpen <= 3 ? '#fde7c8' : 'rgba(255,255,255,.85)',
                    color: minOpen === 0 ? '#ffffff' : minOpen <= 3 ? '#8a4b08' : STATUS_COLORS.open,
                  }}>
                    {minOpen === 0 ? 'FULL' : minOpen}
                  </span>
                ) : null}
              </>
            ) : (<>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0, gap: 6 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              minWidth: 0,
              overflow: 'hidden',
            }}>
              <span style={{ fontSize: '13px', flexShrink: 0 }}>{style.icon}</span>
              <span style={{
                fontWeight: 900,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {barLabel(variant)}
              </span>
              {notOnPortal && (
                <span className="rounded px-1 font-black whitespace-nowrap"
                  style={{ background: '#2b2224', color: '#ffffff', fontSize: 9, lineHeight: '13px' }}>
                  NOT ON PORTAL
                </span>
              )}
            </div>
            {status && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 900,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  marginLeft: '8px',
                  color: statusColor,
                }}
              >
                {status.text}
              </span>
            )}
            </div>
            {perDayCells && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${perDayCells.length}, 1fr)`, gap: 2 }}>
                {perDayCells.map((d, i) => {
                  if (!d) return <span key={i} />;
                  const open = Number(d.open || 0);
                  const full = open === 0;
                  const low = !full && open <= 3;
                  return (
                    <span
                      key={i}
                      title={`${d.sqlDate}: ${d.occ} signed up, ${open} open`}
                      style={{
                        textAlign: 'center', fontSize: 10.5, fontWeight: 900, lineHeight: '15px', borderRadius: 4,
                        background: full ? '#c0392b' : low ? '#fde7c8' : 'rgba(255,255,255,.85)',
                        color: full ? '#ffffff' : low ? '#8a4b08' : '#1f2a33',
                        whiteSpace: 'nowrap', overflow: 'hidden',
                      }}
                    >
                      {full ? 'FULL' : open}
                    </span>
                  );
                })}
              </div>
            )}
            </>)}
          </div>
        );
      })}
    </div>
  );
}
